import type { CalendarDate } from '~/types/dates'
import type { DomainGoalRecord, FitnessDomain } from './fitnessDomain'
import { FITNESS_DOMAIN_LABELS, isParticipating, isPlanned } from './fitnessDomain'
import type { TrainingSessionCounts } from './trainingWeek'

/**
 * What this week amounts to so far (D-099 §C and §D).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE CHECKLIST IS DERIVED. THERE IS NO SETTINGS EDITOR AND THERE MUST NOT BE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  It is computed from five things at once: the active domains, the current
 *  body-composition phase, the confirmed weekly plan, the available integrations
 *  and the evidence the applicable rules genuinely require.
 *
 *  **A Settings form for the items or their counts would be a second, competing
 *  representation of the plan**, and the app would have to decide which one was
 *  true every time they disagreed. Changing what a week contains means editing
 *  the plan in My Plan. This is the same argument that makes `stepsFor` the
 *  single authority on setup steps and `isJudgeableAnswer` the single authority
 *  on what counts as an answer.
 *
 *  **Two kinds of item, and the distinction is the whole of D-099 §D.** Engine
 *  evidence requirements are fixed versioned rules — the user cannot lower the
 *  weigh-in count, because it is not a statement about what they intend to do
 *  but about what the engine needs before it will draw a conclusion. A user who
 *  set it to one would not get a gentler week; they would get an app that
 *  silently stopped being able to advise them. Personal commitments come from
 *  the plan and are changed there.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  INACTIVE DOMAINS CONTRIBUTE NOTHING, AND THIS IS THE THING MOST LIKELY TO
 *  BE GOT WRONG
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A fixed six-item list would show a mobility-only user four items they were
 *  never asked to do — four apparent failures on the one screen whose entire job
 *  is not to do that. Every item below is produced by something the user
 *  actually opted into.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NO SCORE, IN ANY FORM
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  No streak, no grade, no percentage, no red failure state, no "failed week"
 *  and no "3 of 5 done". `soFar` and `target` are separate numbers **so that a
 *  renderer is never handed a ratio** — the shape of the data is what makes the
 *  prohibition hard to violate by accident.
 *
 *  Missing evidence explains what Project Sola cannot conclude. A missed planned
 *  behaviour is reported neutrally. **And completing everything does not mean
 *  the plan worked** — the checklist describes what was collected, never whether
 *  it was any good, which is the rules engine's job and a different sentence.
 */

export type ChecklistItemSource = 'evidence' | 'commitment' | 'review' | 'nutrition'

export type ChecklistItemState = 'collected' | 'partiallyCollected' | 'notYet'

export interface WeekChecklistItem {
  readonly id: string
  readonly label: string
  readonly source: ChecklistItemSource
  readonly domain?: FitnessDomain
  readonly target: number
  readonly soFar: number
  readonly state: ChecklistItemState
  /**
   * What Project Sola cannot conclude yet.
   *
   * **Never what the user failed.** Present only on evidence items, because a
   * missed personal commitment has no consequence for the engine — it is the
   * user's week, and the app has no opinion about it beyond reporting it.
   */
  readonly consequence?: string
}

export interface WeekChecklist {
  readonly weekStartDate: CalendarDate
  readonly items: readonly WeekChecklistItem[]
  /** Whether the plan contains any weekly commitment at all. */
  readonly hasPlannedItems: boolean
}

export interface WeekChecklistCounts {
  readonly weighIns: number
  readonly measurementSessions: number
  readonly nutritionDaysAnswered: number
  readonly reviewFinalized: boolean
  /**
   * What each domain's materialised week planned and completed (D-105 §3).
   *
   * **Absent for a domain means there is no materialised week for it**, not that
   * nothing was done — a legacy goal whose plan is a `weeklyCommitment` quota has
   * no per-session record to count, and falls back to describing the plan with
   * nothing recorded against it. That fallback is what keeps an old week reading
   * exactly as it always did.
   */
  readonly trainingByDomain: Readonly<Partial<Record<FitnessDomain, TrainingSessionCounts>>>
}

export interface WeekChecklistInput {
  readonly weekStartDate: CalendarDate
  readonly goals: readonly DomainGoalRecord[]
  readonly bodyCompositionPhaseActive: boolean
  readonly nutritionDailyChecksRequired: boolean
  readonly counts: WeekChecklistCounts
  /**
   * Injected, never imported from `~/rules/thresholds`.
   *
   * Keeps this file free of a rules dependency and matches the standing rule
   * that a pure module takes its reference date and thresholds rather than
   * reaching for them. The value itself is D-036's approved four, and **D-099
   * introduces no new threshold**.
   */
  readonly thresholds: { readonly weighInsRequired: number }
}

const DAYS_IN_WEEK = 7

/**
 * What this domain planned this week, and what has been recorded against it.
 *
 * **Three sources, in order of how much they actually know.** The materialised
 * week is what this week really contained, frozen when it began, and it wins
 * wherever it exists. A goal with a schedule but no materialised week — a week
 * the app was never opened in — describes the schedule and records nothing
 * against it. A legacy goal with neither keeps its quota exactly as before.
 *
 * **The middle case cannot report progress and must not pretend to.** Nothing
 * was recorded because there was nothing to record against, which is *not yet*
 * rather than a failure — the same distinction the rest of this file draws.
 */
function plannedAndCompleted(
  goal: DomainGoalRecord,
  materialised: TrainingSessionCounts | undefined,
): TrainingSessionCounts {
  if (materialised !== undefined) return materialised

  const schedule = goal.weeklySchedule ?? []
  if (schedule.length > 0) return { planned: schedule.length, completed: 0 }

  const standard = goal.weeklyCommitment?.standard ?? []
  return {
    planned: standard.reduce((total, entry) => total + entry.timesPerWeek, 0),
    completed: 0,
  }
}

function stateFor(soFar: number, target: number): ChecklistItemState {
  if (soFar >= target) return 'collected'
  return soFar === 0 ? 'notYet' : 'partiallyCollected'
}

function item(
  fields: Omit<WeekChecklistItem, 'state'> & { readonly state?: ChecklistItemState },
): WeekChecklistItem {
  return { ...fields, state: fields.state ?? stateFor(fields.soFar, fields.target) }
}

/**
 * Build the week's description.
 *
 * Pure and total: the same inputs always produce the same list, and every item
 * traces to something the user opted into.
 */
export function buildWeekChecklist(input: WeekChecklistInput): WeekChecklist {
  const participating = input.goals.filter(isParticipating)
  const items: WeekChecklistItem[] = []

  /* ── Engine evidence, and only for domains that generate it ────────────── */

  const bodyComposition = participating.find((goal) => goal.domain === 'bodyComposition')

  if (bodyComposition !== undefined && input.bodyCompositionPhaseActive) {
    items.push(
      item({
        id: 'evidence:weighIns',
        label: 'Weigh-ins',
        source: 'evidence',
        domain: 'bodyComposition',
        target: input.thresholds.weighInsRequired,
        soFar: input.counts.weighIns,
        consequence:
          'With fewer than this, Project Sola cannot compare this week with the last one.',
      }),
      item({
        id: 'evidence:measurement',
        label: 'Waist measurement',
        source: 'evidence',
        domain: 'bodyComposition',
        target: 1,
        soFar: input.counts.measurementSessions,
        consequence: 'Without one, the waist trend has nothing new to read.',
      }),
    )
  }

  /* ── Nutrition, only with an active plan that asks daily (D-096) ───────── */

  if (input.nutritionDailyChecksRequired) {
    items.push(
      item({
        id: 'nutrition:dailyChecks',
        label: 'Daily nutrition answers',
        source: 'nutrition',
        target: DAYS_IN_WEEK,
        soFar: input.counts.nutritionDaysAnswered,
        consequence:
          'Unanswered days are missing evidence rather than days off plan, so the week reads as less complete than it may have been.',
      }),
    )
  }

  /* ── Personal commitments, from the confirmed plan ─────────────────────── */

  let hasPlannedItems = false

  for (const goal of participating) {
    if (!isPlanned(goal)) continue

    const training = plannedAndCompleted(goal, input.counts.trainingByDomain[goal.domain])
    if (training.planned === 0) continue
    hasPlannedItems = true

    items.push(
      item({
        id: `commitment:${goal.domain}`,
        label: `${FITNESS_DOMAIN_LABELS[goal.domain]} sessions`,
        source: 'commitment',
        domain: goal.domain,
        target: training.planned,
        soFar: training.completed,
        /* No consequence. A missed personal commitment has none for the engine,
           and inventing one would be the app forming an opinion about the
           user's week that no rule produced. */
      }),
    )
  }

  /* ── The weekly review, whenever an opinion is expected ────────────────── */

  if (participating.length > 0) {
    items.push(
      item({
        id: 'review:weekly',
        label: 'Weekly review',
        source: 'review',
        target: 1,
        soFar: input.counts.reviewFinalized ? 1 : 0,
        consequence: 'The review is where Project Sola gives you its answer for the week.',
      }),
    )
  }

  return { weekStartDate: input.weekStartDate, items, hasPlannedItems }
}
